import { sponsor } from "@/features/sponsor";
import {
  normalizeString,
  trackLeanAnalytics as trackCatalogAnalytics,
} from "@/shared";
import type { ViewportTrackingData } from "@dafiti/analytics-sdk";
import {
  deviceUUIDEvent,
  pageViewEvent,
  searchEvent as sdkSearchEvent,
  selectContentEvent,
  selectItemEvent,
  storeCountryEvent,
  viewItemEvent,
  viewItemListEvent,
} from "@dafiti/analytics-sdk";
import { env } from "next-runtime-env";

import { isActiveFilterValue } from "../helpers/pendingFilters.helper";
import type {
  CatalogMetaResponse,
  CatalogProduct,
} from "../types/catalog.type";
import type { PendingFilters } from "../types/filters.type";

// ── Analytics API (GA-parity events) ─────────────────────────────────────────
//
// Mirrors each GA4 dataLayer event to the internal analytics API via the shared
// `trackCatalogAnalytics`. store_country / device_uuid are intentionally
// excluded: they are device/identity metadata, not behavioral events.

export const pageView = ({ meta }: { meta: CatalogMetaResponse }) => {
  const catalogSegment = meta.tracking_context?.catalog_segment;
  const pageTitle = catalogSegment?.length
    ? catalogSegment.join(" - ")
    : "ecommerce - product list";

  pageViewEvent({
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: pageTitle,
  });

  trackCatalogAnalytics("page_view", {
    page_path: window.location.pathname,
    page_title: pageTitle,
  });
};

export type SearchEventParams = {
  searchTerm: string;
  resultsQuantity: number;
  searchType: string;
};

export function searchEvent(params: SearchEventParams): void {
  sdkSearchEvent({
    search_term: params.searchTerm,
    results_quantity: params.resultsQuantity,
    search_type: params.searchType,
  });

  trackCatalogAnalytics("search", {
    search_term: params.searchTerm,
    results_quantity: params.resultsQuantity,
    search_type: params.searchType,
  });
}

type EcommerceParams = Parameters<typeof viewItemEvent>[0];

type CatalogEcommerceItems = EcommerceParams["ecommerce"]["items"][number] & {
  tracking_data?: string;
  is_sponsored?: boolean;
};

type CatalogEcommerce = Omit<EcommerceParams["ecommerce"], "items"> & {
  items: CatalogEcommerceItems[];
};

const getCatalogEcommerceItem = ({
  product,
  listName,
  index,
}: {
  product: CatalogProduct;
  listName: string;
  index: number;
}): CatalogEcommerceItems => {
  const sponsoredAdsEnabled = sponsor.isSponsoredAdsEnabled();
  const country = env("NEXT_PUBLIC_COUNTRY")?.toLowerCase();

  return {
    item_id: normalizeString(product.sku),
    item_name: normalizeString(product.name),
    item_brand: normalizeString(product.brand),
    item_category: normalizeString(product.breadcrumbs[1]?.name),
    item_category2: normalizeString(product.breadcrumbs[2]?.name),
    item_category3: normalizeString(product.breadcrumbs[3]?.name),
    price: product.price.value,
    discount: product.price.discount_value || 0,
    item_variant: normalizeString(product.color),
    affiliation: `dafiti ${country} - ${product.seller?.name ?? ""}`,
    index,
    quantity: 1,
    is_sponsored:
      sponsoredAdsEnabled && (product.sponsor?.is_sponsored ?? false),
    item_list_name: listName,
    tracking_data: sponsoredAdsEnabled
      ? product.sponsor?.tracking_data
      : undefined,
  };
};

export const catalogView = ({
  trackingData,
  catalogMeta,
}: {
  trackingData: ViewportTrackingData<CatalogProduct>[];
  catalogMeta?: CatalogMetaResponse;
}) => {
  const itemListName =
    catalogMeta?.tracking_context?.catalog_segment?.join(" - ") || "catalog";

  const products = trackingData
    .map((t) => t.data!)
    .sort(
      (a, b) =>
        ((a as CatalogProduct & { index?: number }).index ?? 1) -
        ((b as CatalogProduct & { index?: number }).index ?? 1),
    );

  sponsor.trackProductImpression(products);

  viewItemListEvent({
    ecommerce: {
      items: products.map((product) =>
        getCatalogEcommerceItem({
          product,
          index: (product as CatalogProduct & { index?: number }).index ?? 1,
          listName: itemListName,
        }),
      ),
    } as CatalogEcommerce,
  });

  trackCatalogAnalytics("view_item_list", {
    item_list_name: itemListName,
    items_quantity: products.length,
  });
};

export const catalogProductClick = ({
  product,
  index,
  catalogMeta,
}: {
  product: CatalogProduct;
  index?: number;
  catalogMeta?: CatalogMetaResponse;
}) => {
  sponsor.trackProductClick(product);

  const itemListName =
    catalogMeta?.tracking_context?.catalog_segment?.join(" - ") || "catalog";

  selectItemEvent({
    ecommerce: {
      items: [
        getCatalogEcommerceItem({
          product,
          index: index ?? 1,
          listName: itemListName,
        }),
      ],
    } as CatalogEcommerce,
  });

  trackCatalogAnalytics("select_item", {
    item_id: normalizeString(product.sku),
    item_list_name: itemListName,
    index: index ?? 1,
  });
};

export const catalogSortClick = (trackingValue: string) => {
  selectContentEvent({
    content_type: "ordination",
    content_id: trackingValue,
  });

  trackCatalogAnalytics("select_content", {
    content_type: "ordination",
    content_id: trackingValue,
  });
};

export const catalogPaginationClick = () => {
  selectContentEvent({
    content_type: "product list",
    content_id: "pagination",
    complement: "click",
  });

  trackCatalogAnalytics("select_content", {
    content_type: "product list",
    content_id: "pagination",
    complement: "click",
  });
};

export const catalogFilterClick = (fields: string[]) => {
  if (fields.length === 0) return;

  const content_id = `filter ${fields.join(", ")}`;

  selectContentEvent({
    content_type: "product list",
    content_id,
    complement: "click",
  });

  trackCatalogAnalytics("select_content", {
    content_type: "product list",
    content_id,
    complement: "click",
  });
};

const FIELD_NAME_BY_STORE_KEY: Record<string, string> = {
  categories_ids: "category",
  price_min: "price",
  price_max: "price",
};

const EXCLUDED_STORE_KEYS = new Set(["pending_category_path"]);

export const extractAppliedFilterFields = (
  pendingFilters: PendingFilters | null,
): string[] => {
  if (!pendingFilters) return [];

  const fields: string[] = [];
  const seen = new Set<string>();

  for (const [key, value] of Object.entries(pendingFilters)) {
    if (EXCLUDED_STORE_KEYS.has(key)) continue;
    if (!isActiveFilterValue(value)) continue;

    const fieldName = FIELD_NAME_BY_STORE_KEY[key] ?? key;
    if (seen.has(fieldName)) continue;

    seen.add(fieldName);
    fields.push(fieldName);
  }

  return fields;
};

export const storeCountry = (country: string) => {
  storeCountryEvent({ store_country: country });
};

export const deviceUUID = () => {
  deviceUUIDEvent();
};
